// src/components/InviteModal.js
import React, { useEffect, useState } from "react";
import { standardUserApi } from "../services/standardUserApi";
import mixpanel from "mixpanel-browser";

/**
 * Shared "invite someone to join" modal. Used from a booked gathering
 * card on My Gatherings (gatheringId set, share text can mention the
 * date) and from a theme's own landing page (gatheringId null, general
 * invite to that theme).
 */
const InviteModal = ({ isOpen, onClose, themeId, themeName, gatheringId, gatheringDateLabel }) => {
  const [inviteUrl, setInviteUrl] = useState("");
  // A/B test decided server-side, once, when this link is first created,
  // and fixed thereafter (see GatheringInviteLink.share_text_prefilled) -
  // true prefills the full explanatory message below, false prefills just
  // the bare link, in case the explanatory text makes people hesitant to
  // share at all.
  const [includeText, setIncludeText] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    if (!isOpen || !themeId) return;
    setInviteUrl("");
    setError("");
    setIsLoading(true);
    const token = localStorage.getItem("memberstackToken");
    standardUserApi
      .createInviteLink(token, { themeId, gatheringId: gatheringId || undefined })
      .then((data) => {
        setInviteUrl(data.url);
        setIncludeText(data.includeText !== false);
      })
      .catch(() => setError("Couldn't create your invite link. Please try again."))
      .finally(() => setIsLoading(false));
  }, [isOpen, themeId, gatheringId]);

  if (!isOpen) return null;

  const explanatoryText = gatheringDateLabel
    ? `I'm heading to an OkGather ${themeName} gathering on ${gatheringDateLabel} and thought you might want to sign up and join too. Here's the link: ${inviteUrl} (you'll need your own account, no plus-ones on the day)`
    : `I'm going to OkGather's ${themeName} gatherings and thought you might want to check it out too. Here's the link: ${inviteUrl} (you'll need your own account, no plus-ones on the day)`;
  const whatsappText = includeText ? explanatoryText : inviteUrl;

  const trackShare = (method) => {
    if (mixpanel && mixpanel.__loaded) {
      mixpanel.track("Invite Link Shared", {
        method,
        theme_name: themeName,
        has_gathering: !!gatheringId,
        include_text: includeText,
      });
    }
  };

  const handleWhatsapp = () => {
    trackShare("whatsapp");
    window.open(`https://wa.me/?text=${encodeURIComponent(whatsappText)}`, "_blank", "noopener,noreferrer");
  };

  const handleCopy = () => {
    if (!inviteUrl) return;
    navigator.clipboard.writeText(inviteUrl).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
      trackShare("copy_link");
    });
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(17, 24, 39, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
        padding: "16px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: "16px",
          padding: "28px",
          maxWidth: "440px",
          width: "100%",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        }}
      >
        <h3 style={{ margin: "0 0 12px", color: "#1F2937", fontSize: "1.25rem", fontWeight: "700" }}>
          🔗 Invite someone to join
        </h3>
        <p style={{ color: "#4B5563", fontSize: "0.95rem", lineHeight: "1.5", marginBottom: "20px" }}>
          Most people come along to OkGather by themselves. The whole point is meeting new people, so there's nothing wrong with going solo. But if you'd like, you're welcome to invite someone to sign up and join too. They can't come as a plus-one on the day, they'll need to sign up separately with their own account.
        </p>

        {isLoading && <p style={{ color: "#9CA3AF", fontSize: "0.9rem" }}>Creating your link...</p>}
        {error && <p style={{ color: "#EF4444", fontSize: "0.9rem" }}>{error}</p>}

        {!isLoading && !error && inviteUrl && (
          <>
            <button
              onClick={handleWhatsapp}
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "10px",
                border: "none",
                backgroundColor: "#25D366",
                color: "#fff",
                fontWeight: "700",
                fontSize: "1rem",
                cursor: "pointer",
                marginBottom: "10px",
              }}
            >
              Share via WhatsApp
            </button>

            <div style={{ display: "flex", gap: "8px" }}>
              <input
                type="text"
                value={inviteUrl}
                readOnly
                style={{
                  flex: 1,
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: "1.5px solid #E5E7EB",
                  fontSize: "0.85rem",
                  color: "#4B5563",
                }}
              />
              <button
                onClick={handleCopy}
                style={{
                  padding: "10px 16px",
                  borderRadius: "8px",
                  border: "1.5px solid #8B5FBF",
                  backgroundColor: "#fff",
                  color: "#8B5FBF",
                  fontWeight: "600",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {copySuccess ? "Copied!" : "Copy"}
              </button>
            </div>
          </>
        )}

        <button
          onClick={onClose}
          style={{
            marginTop: "20px",
            background: "none",
            border: "none",
            color: "#9CA3AF",
            fontSize: "0.9rem",
            cursor: "pointer",
            width: "100%",
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default InviteModal;
